import { t } from 'i18next';
import { css, html, LitElement, PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { debounce } from 'lodash-es';

import { OrderStatus } from '@/api-sdk';
import { dataPrintProofStatus } from '@/data';
import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, formStyles, rootVariablesStyles } from '@/styles';
import { SelectOption, SelectVersion } from '@/types';

import { global, GlobalComponentType } from '@/context';
import { consume } from '@lit/context';
import '../form/fg-select';

@customElement('fg-order-history-filters')
export class FgOrderHistoryFilter extends LitElement {
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @state()
  searchString?: string;

  @state()
  status?: OrderStatus;

  setSearchParams(searchString?: string, status?: OrderStatus | 'all') {
    this.searchString = searchString !== '' ? searchString : undefined;
    this.status = status !== 'all' ? status : undefined;
  }

  debounceSearchInput = debounce((value: string) => {
    this.setSearchParams(value, this.status);
  }, 300);

  translatedStatusData() {
    const statusData: SelectOption[] = [];
    dataPrintProofStatus.map(status => {
      statusData.push({
        value: status.value,
        text: t(status.text),
      });
    });
    return statusData;
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (
      _changedProperties.has('searchString') ||
      _changedProperties.has('status')
    ) {
      this.dispatchEvent(
        new CustomEvent<{
          searchString?: string;
          status?: OrderStatus;
        }>('on-filter', {
          detail: {
            searchString: this.searchString,
            status: this.status,
          },
        }),
      );
    }
  }

  render() {
    return html`
      <div>
        <fieldset>
          <label>${t('labels.search')}</label>
          <input
            placeholder=${t('labels.placeholderOrderHistorySearch') || ''}
            @input=${(event: { target: HTMLInputElement }) =>
              this.debounceSearchInput(event.target.value)}
          />
        </fieldset>
        <fieldset>
          <label>${t('labels.status')}</label>

          <fg-select
            part=${this.globalContext.layout}
            name="status-select"
            .defaultValue=${this.status || 'all'}
            .version=${SelectVersion.SINGLE_SELECT}
            .options=${this.translatedStatusData()}
            @options-selected=${(
              event: CustomEvent<{ value: OrderStatus | 'all' }>,
            ) => this.setSearchParams(this.searchString, event.detail.value)}
          ></fg-select>
        </fieldset>
      </div>
    `;
  }
  static styles = [
    baseStyles,
    rootVariablesStyles,
    formStyles,
    css`
      div {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
      }

      fieldset {
        border: none;
        padding: 0;
        margin: 0;
        width: 100%;
      }

      fieldset input,
      fieldset fg-select {
        width: 100%;
      }

      fieldset:first-of-type {
        margin-right: 8px;
      }

      label {
        font-size: 0.875rem;
        color: var(--fg-primary, var(--fg-fallback-primary));
        line-height: 160%;
      }

      :host([part='retail']) input {
        min-height: 48px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        div {
          flex-direction: row;
          gap: 0;
        }

        fieldset:first-of-type {
          width: 60%;
        }

        fieldset:nth-of-type(2) {
          width: 40%;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        fieldset:first-of-type {
          width: 70%;
        }

        fieldset:nth-of-type(2) {
          width: 30%;
        }
      }
    `,
  ];
}
